import { css } from '@emotion/react'
import { colors } from 'plume-ui'
import { SearchCondition } from './SearchCondition'
import { OpeningCondition, Term } from '../SearchConditionList'
import { formatDate, dateHyphenFormatPattern } from '@/lib/date'
import { parse, format, startOfYear, endOfYear, subYears } from 'date-fns'
import { CheckBox } from './Checkbox'
import { LocalDatePicker } from '../../../common/LocalDatePicker'

type Props = {
  term: Term
  setTerm: React.Dispatch<React.SetStateAction<Term>>
  opening: boolean
  setOpeningCondition: React.Dispatch<React.SetStateAction<OpeningCondition>>
}

type PresetTerms = 'thisYear' | 'lastYear' | 'reset'

export const TermFilter: React.FC<Props> = (props) => {
  const minDate = new Date('2000-01-01')
  const maxDate = new Date()
  const startOfThisYear = format(
    startOfYear(new Date()),
    dateHyphenFormatPattern
  )
  const endOfThisYear = format(endOfYear(new Date()), dateHyphenFormatPattern)
  const startOfLastYear = format(
    startOfYear(subYears(new Date(), 1)),
    dateHyphenFormatPattern
  )
  const endOfLastYear = format(
    endOfYear(subYears(new Date(), 1)),
    dateHyphenFormatPattern
  )

  const setTermFrom = (from: string) => {
    props.setTerm({ from, to: props.term.to })
  }

  const setTermTo = (to: string) => {
    props.setTerm({ from: props.term.from, to })
  }
  const presetThisYearTerm = { from: startOfThisYear, to: endOfThisYear }
  const presetLastYearTerm = { from: startOfLastYear, to: endOfLastYear }
  const isSelectedPreset = (preset: Term) => {
    return preset.from === props.term.from && preset.to === props.term.to
  }

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setTermFrom(formatDate(date.toISOString()))
    }
  }

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setTermTo(formatDate(date.toISOString()))
    }
  }

  return (
    <SearchCondition
      title="期間"
      selected={!!(props.term.from || props.term.to)}
      opening={props.opening}
      toggleOpen={() => {
        if (props.opening) {
          props.setOpeningCondition(null)
        } else {
          props.setOpeningCondition('Term')
        }
      }}
    >
      <TermCheckBox
        value={'thisYear'}
        handler={() => {
          props.setTerm(presetThisYearTerm)
        }}
        checked={isSelectedPreset(presetThisYearTerm)}
      >
        今年
      </TermCheckBox>
      <TermCheckBox
        value={'lastYear'}
        handler={() => {
          props.setTerm(presetLastYearTerm)
        }}
        checked={isSelectedPreset(presetLastYearTerm)}
      >
        去年
      </TermCheckBox>
      <TermCheckBox
        value={'reset'}
        handler={() => {
          props.setTerm({ from: null, to: null })
        }}
        checked={false}
      >
        リセット
      </TermCheckBox>
      <div
        css={css`
          padding: 5px 10px 5px 25px;
          display: flex;
          justify-content: space-around;
          align-items: flex-start;
        `}
      >
        <div>
          <LocalDatePicker
            selected={
              props.term.from
                ? parse(props.term.from, 'yyyy-MM-dd', new Date())
                : null
            }
            onChange={(date: Date | null /* selectsRange={false} */) => {
              handleStartDateChange(date)
            }}
            maxDate={maxDate}
            minDate={minDate}
          />
          <div
            css={css`
              text-align: right;
              color: ${colors.textLight};
              margin-top: 3px;
              font-size: 11px;
            `}
          >
            (以降)
          </div>
        </div>
        <span
          css={css`
            display: inline-block;
            color: ${colors.gray90};
            margin: 4px 5px;
          `}
        >
          または
        </span>
        <div>
          <LocalDatePicker
            selected={
              props.term.to
                ? parse(props.term.to, 'yyyy-MM-dd', new Date())
                : null
            }
            onChange={(date: Date | null /* selectsRange={false} */) => {
              handleEndDateChange(date)
            }}
            maxDate={maxDate}
            minDate={minDate}
          />
          <div
            css={css`
              text-align: right;
              color: ${colors.textLight};
              margin-top: 3px;
              font-size: 11px;
            `}
          >
            (以前)
          </div>
        </div>
      </div>
    </SearchCondition>
  )
}

type TermCheckBoxProps = {
  children: React.ReactNode
  value: PresetTerms
  checked: boolean
  handler: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const TermCheckBox: React.FC<TermCheckBoxProps> = (props) => {
  return (
    <CheckBox
      name="terms"
      value={props.value}
      handler={props.handler}
      checked={props.checked}
    >
      {props.children}
    </CheckBox>
  )
}
